import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';

/**
 * Minimal shape of the (non-standard, still-experimental) Network Information API
 * we read to make preloading connection-aware.
 */
interface NetworkInformation {
  /** User has requested reduced data usage. */
  saveData?: boolean;
  /** Effective connection type, e.g. 'slow-2g', '2g', '3g', '4g'. */
  effectiveType?: string;
}

/**
 * Preloads lazy route chunks into cache shortly after load — so a later navigation
 * still finds the current build's chunks even once the tab has gone stale — while
 * staying considerate of the network. Skips preloading when the connection is
 * data-saving or 2g-class, or when a route opts out via `data: { preload: false }`.
 */
@Injectable({ providedIn: 'root' })
export class NetworkAwarePreloadStrategy implements PreloadingStrategy {
  /**
   * Decides whether to preload a given route now.
   * @param route the candidate route
   * @param load triggers the route's lazy load
   * @returns the load stream when preloading, otherwise a no-op stream
   */
  public preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (route.data?.['preload'] === false) {
      return of(null);
    }
    const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
    if (connection?.saveData) {
      return of(null);
    }
    if (/(^|-)2g$/.test(connection?.effectiveType ?? '')) {
      return of(null);
    }
    return load();
  }
}
